import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import { User, AuthContextType } from '@/types/auth';
import AuthService from '@/services/api/auth';
import { setBearerTokenFromLocalStorage } from '@/utils/axios';
import { storage } from '@/utils/storage';
import { APP_CONFIG, isAllowedEmail } from '@/config/app';

/** Decode a JWT payload without verifying the signature */
const decodeJWT = (token: string): Record<string, any> | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isValidDomain = (email: string | null): boolean => {
  if (!email) return false;
  // Individually approved addresses may sign in regardless of domain.
  if (isAllowedEmail(email)) return true;
  if (!APP_CONFIG.allowedEmailDomain) return true;
  return email.endsWith(APP_CONFIG.allowedEmailDomain);
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user && !!token;

  useEffect(() => {
    const checkLocalStorageAuth = () => {
      try {
        const storedToken = storage.getItem('token');
        const savedUser = storage.getItem('profile');

        if (storedToken && savedUser) {
          const parsedUser = JSON.parse(savedUser);
          if (parsedUser && parsedUser.email && parsedUser.uid) {
            setBearerTokenFromLocalStorage();
            setToken(storedToken);
            setUser(parsedUser);
          } else {
            AuthService.logout();
            setUser(null);
            setToken(null);
          }
        } else if (storedToken && !savedUser) {
          const payload = decodeJWT(storedToken);
          if (payload && payload.identity?.email) {
            const recovered = {
              uid: payload.identity.uuid || '',
              email: payload.identity.email,
              displayName: payload.identity.name || payload.identity.email,
              photoURL: null,
            };
            storage.setItem('profile', JSON.stringify(recovered));
            setBearerTokenFromLocalStorage();
            setToken(storedToken);
            setUser(recovered);
          } else {
            AuthService.logout();
            setUser(null);
            setToken(null);
          }
        } else {
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        console.error('Critical error in auth check:', error);
        AuthService.logout();
        setUser(null);
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkLocalStorageAuth();
  }, []);

  // Runtime session integrity watcher — re-checks on interval, focus, and visibility
  useEffect(() => {
    if (!user || !token) return;

    const checkSessionIntegrity = () => {
      const storedUserRaw = storage.getItem('profile');
      let isValid = false;
      if (storedUserRaw) {
        try {
          const storedUser = JSON.parse(storedUserRaw);
          isValid = !!(storedUser && storedUser.email);
        } catch {
          isValid = false;
        }
      }
      if (!isValid) {
        // Try to recover profile from the in-memory token before logging out
        const payload = decodeJWT(token!);
        if (payload && payload.identity?.email) {
          const recovered = {
            uid: payload.identity.uuid || '',
            email: payload.identity.email,
            displayName: payload.identity.name || payload.identity.email,
            photoURL: null,
          };
          storage.setItem('profile', JSON.stringify(recovered));
          setUser(recovered);
        } else {
          console.warn(
            '[AuthContext] Session integrity check failed — forcing sign out'
          );
          AuthService.logout();
          setUser(null);
          setToken(null);
          if (typeof window !== 'undefined') {
            window.location.href = '/signin';
          }
        }
      }
    };

    const interval = setInterval(checkSessionIntegrity, 60_000);
    const onFocus = () => checkSessionIntegrity();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkSessionIntegrity();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, token]);

  const signInWithGoogle = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    // Focus-based popup detection
    let focusHandler: (() => void) | null = null;
    let finished = false;
    const cleanup = () => {
      if (focusHandler) {
        window.removeEventListener('focus', focusHandler);
        focusHandler = null;
      }
    };

    focusHandler = () => {
      if (!finished) {
        setError('Sign-in cancelled.');
        showErrorToast('Error', 'Sign-in cancelled.');
        setTimeout(() => setIsLoading(false), 100);
        cleanup();
      }
    };
    window.addEventListener('focus', focusHandler);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      finished = true;
      cleanup();
      const firebaseUser = result.user;

      if (!isValidDomain(firebaseUser.email)) {
        await firebaseSignOut(auth);
        throw new Error(
          `Please use a valid ${APP_CONFIG.allowedEmailDomain} account.`
        );
      }

      const firebaseToken = await firebaseUser.getIdToken();
      const backendToken = await AuthService.login(firebaseToken);

      const userData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        firstName: firebaseUser.displayName?.split(' ')[0] || '',
        lastName: firebaseUser.displayName?.split(' ')[1] || '',
        isActive: true,
        lastLogin: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setUser(userData);
      setToken(backendToken);

      storage.setItem('profile', JSON.stringify(userData));
      storage.setItem('token', backendToken);

      setBearerTokenFromLocalStorage();
    } catch (err: any) {
      finished = true;
      cleanup();
      // Handle popup closed by user
      if (err && err.code === 'auth/popup-closed-by-user') {
      } else if (err && err.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups and try again.');
        showErrorToast(
          'Error',
          'Popup blocked. Please allow popups and try again.'
        );
      } else {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to sign in with Google';
        setError(errorMessage);
        showErrorToast('Error', errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await firebaseSignOut(auth);

      setUser(null);
      setToken(null);
      AuthService.logout();

      if (window.axios) {
        delete window.axios.defaults.headers.common['Authorization'];
      }

      showSuccessToast('Signed out', 'Successfully signed out!');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to sign out';
      setError(errorMessage);
      showErrorToast('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    signInWithGoogle,
    signOut,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

declare global {
  interface Window {
    axios?: any;
  }
}
